import { useState } from 'react';
import { Button, Card, Input, Typography, message, Divider } from 'antd';
import {
  parseWorkflowConfig,
  validateWorkflowConfig,
} from '@refly-packages/ai-workspace-common/utils/workflow-config-parser';
import { WorkflowRunForm } from './workflow-run/workflow-run-form';

const { TextArea } = Input;
const { Title, Text } = Typography;

interface WorkflowConfigParserProps {
  onConfigParsed?: (config: {
    title: string;
    tasks: any[];
    variables: any[];
  }) => void;
}

export const WorkflowConfigParser: React.FC<WorkflowConfigParserProps> = ({ onConfigParsed }) => {
  const [configInput, setConfigInput] = useState('');
  const [parsedConfig, setParsedConfig] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleParse = () => {
    try {
      setLoading(true);
      setError(null);

      const config = JSON.parse(configInput);

      // 验证配置
      if (!validateWorkflowConfig(config)) {
        throw new Error('无效的工作流配置格式');
      }

      // 解析配置
      const result = parseWorkflowConfig(config);
      setParsedConfig(result);

      // 通知父组件
      if (onConfigParsed) {
        onConfigParsed(result);
      }

      message.success('工作流配置解析成功');
    } catch (err) {
      setError(err instanceof Error ? err.message : '解析失败，请检查配置格式');
      message.error('工作流配置解析失败');
    } finally {
      setLoading(false);
    }
  };

  const handleRunWorkflow = async (variables: any[]) => {
    message.info('工作流执行逻辑将在这里实现');
    console.log('执行工作流，变量:', variables);
  };

  return (
    <Card title="工作流配置解析器" className="w-full max-w-4xl mx-auto">
      <div className="space-y-4">
        <div>
          <Title level={5}>输入工作流配置</Title>
          <TextArea
            value={configInput}
            onChange={(e) => setConfigInput(e.target.value)}
            placeholder="请输入工作流配置 JSON"
            rows={10}
            className="font-mono"
          />
        </div>

        <Button
          type="primary"
          onClick={handleParse}
          loading={loading}
          disabled={!configInput.trim()}
        >
          解析配置
        </Button>

        {error && (
          <div className="text-red-500">
            <Text type="danger">{error}</Text>
          </div>
        )}

        {parsedConfig && (
          <>
            <Divider />
            <Title level={5}>解析结果</Title>

            <div className="space-y-4">
              <div>
                <Text strong>工作流标题:</Text> {parsedConfig.title}
              </div>

              <div>
                <Text strong>任务:</Text>
                <ul className="list-disc pl-5 mt-1 space-y-1">
                  {parsedConfig.tasks.map((task: any) => (
                    <li key={task.id}>
                      {task.title}
                      {task.dependentTasks && task.dependentTasks.length > 0 && (
                        <Text type="secondary"> (依赖: {task.dependentTasks.join(', ')})</Text>
                      )}
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <Text strong>变量:</Text>
                <div className="mt-2">
                  <WorkflowRunForm
                    workflowVariables={parsedConfig.variables}
                    onSubmitVariables={handleRunWorkflow}
                    loading={false}
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </Card>
  );
};
